/**
 * BILLING STORE
 *
 * Manages billing state: plans, subscriptions, credits, API keys, usage, invoices.
 * Cloud-only — all endpoints require a numeric workspace backend ID.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// snake_case ↔ camelCase helpers
// ---------------------------------------------------------------------------

function snakeToCamel(obj: any): any {
  if (Array.isArray(obj)) return obj.map(snakeToCamel);
  if (obj && typeof obj === "object" && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()),
        snakeToCamel(v),
      ]),
    );
  }
  return obj;
}

function camelToSnake(obj: any): any {
  if (Array.isArray(obj)) return obj.map(camelToSnake);
  if (obj && typeof obj === "object" && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k.replace(/([A-Z])/g, "_$1").toLowerCase(),
        camelToSnake(v),
      ]),
    );
  }
  return obj;
}

/** Map a billing API error to a user-facing error string. */
function billingError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  return msg.includes("403") ? "admin_required" : msg;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BillingPlan {
  id: number;
  name: string;
  displayName: string;
  priceCents: number;
  isPerSeat: boolean;
  creditMarkupPct: number;
  includedCreditsCents: number;
  maxProjects: number | null;
  maxDailyRuns: number | null;
  byokAllowed: boolean;
  features: Record<string, any>;
  stripePriceId: string | null;
  active: boolean;
  createdAt: string;
}

export interface Subscription {
  id: number | null;
  workspaceId: number;
  plan: BillingPlan;
  billingMode: "byok" | "credits";
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  seatCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreditBalance {
  workspaceId: number;
  balanceCents: number;
  lifetimePurchasedCents: number;
  lifetimeUsedCents: number;
  updatedAt: string | null;
}

export interface CreditTransaction {
  id: number;
  workspaceId: number;
  type: string;
  amountCents: number;
  balanceAfterCents: number;
  description: string | null;
  usageEventId: number | null;
  stripePaymentIntentId: string | null;
  createdAt: string;
}

export interface ApiKeyInfo {
  id: number;
  workspaceId: number;
  provider: string;
  keyHint: string | null;
  isValid: boolean;
  lastValidated: string | null;
  createdAt: string;
}

export interface BillingDetails {
  workspaceId: number;
  companyName: string | null;
  billingEmail: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string;
  vatNumber: string | null;
  currency: string;
}

export interface UsageSummary {
  workspaceId: number;
  periodStart: string;
  periodEnd: string;
  totalEvents: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalRawCostCents: number;
  totalBilledCostCents: number;
  byModel: Record<string, { events: number; inputTokens: number; outputTokens: number; costCents: number }>;
  byProject: Record<string, { events: number; costCents: number }>;
  byDay: Record<string, { events: number; costCents: number }>;
}

export interface MemberUsage {
  userId: string;
  name: string | null;
  email: string | null;
  totalEvents: number;
  totalBilledCents: number;
}

export interface Invoice {
  id: number;
  workspaceId: number;
  invoiceNumber: string;
  periodStart: string;
  periodEnd: string;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  currency: string;
  status: string;
  lineItems: any[];
  billingDetails: Record<string, any>;
  stripeInvoiceId: string | null;
  pdfUrl: string | null;
  sentAt: string | null;
  paidAt: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export interface PlanLimits {
  planName: string;
  maxProjects: number | null;
  maxDailyRuns: number | null;
}

interface BillingState {
  plans: BillingPlan[];
  subscription: Subscription | null;
  credits: CreditBalance | null;
  planLimits: PlanLimits | null;
  transactions: CreditTransaction[];
  apiKeys: ApiKeyInfo[];
  billingDetails: BillingDetails | null;
  usage: UsageSummary | null;
  invoices: Invoice[];
  memberUsage: MemberUsage[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchPlans: () => Promise<void>;
  fetchSubscription: (wsId: number) => Promise<void>;
  subscribe: (wsId: number, planName: string, billingMode: string) => Promise<void>;
  cancelSubscription: (wsId: number) => Promise<void>;
  fetchCredits: (wsId: number) => Promise<void>;
  purchaseCredits: (wsId: number, amountCents: number) => Promise<{ clientSecret: string } | null>;
  fetchTransactions: (wsId: number, limit?: number, offset?: number) => Promise<CreditTransaction[]>;
  fetchApiKeys: (wsId: number) => Promise<void>;
  addApiKey: (wsId: number, provider: string, apiKey: string) => Promise<boolean>;
  removeApiKey: (wsId: number, provider: string) => Promise<boolean>;
  fetchBillingDetails: (wsId: number) => Promise<void>;
  updateBillingDetails: (wsId: number, details: Partial<BillingDetails>) => Promise<boolean>;
  fetchUsage: (wsId: number, start?: string, end?: string) => Promise<void>;
  fetchInvoices: (wsId: number, limit?: number, offset?: number) => Promise<Invoice[]>;
  updateSeats: (wsId: number, seatCount: number) => Promise<void>;
  fetchMemberUsage: (wsId: number, start?: string, end?: string) => Promise<void>;
  reset: () => void;
}

const initialState = {
  plans: [] as BillingPlan[],
  subscription: null as Subscription | null,
  credits: null as CreditBalance | null,
  planLimits: null as PlanLimits | null,
  transactions: [] as CreditTransaction[],
  apiKeys: [] as ApiKeyInfo[],
  billingDetails: null as BillingDetails | null,
  usage: null as UsageSummary | null,
  invoices: [] as Invoice[],
  memberUsage: [] as MemberUsage[],
  isLoading: false,
  error: null as string | null,
};

// Request deduplication: prevents concurrent duplicate API calls
let _subInflight: Promise<void> | null = null;
let _subInflightWsId: number | null = null;
let _creditsInflight: Promise<void> | null = null;
let _creditsInflightWsId: number | null = null;

export const useBillingStore = create<BillingState>()(
  persist(
    (set, get) => ({
      ...initialState,

      fetchPlans: async () => {
        try {
          const data = await api.get<any[]>("/api/billing/plans");
          set({ plans: (data || []).map((p) => snakeToCamel(p) as BillingPlan) });
        } catch (e) {
          console.error("[billingStore] fetchPlans failed:", e);
        }
      },

      fetchSubscription: async (wsId) => {
        if (_subInflight && _subInflightWsId === wsId) {
          await _subInflight;
          return;
        }
        const doFetch = async () => {
          set({ isLoading: true, error: null });
          try {
            const data = await api.get<any>(`/api/workspaces/${wsId}/billing/subscription`);
            const sub = snakeToCamel(data) as Subscription;
            const limits: PlanLimits = {
              planName: sub.plan?.name ?? "free",
              maxProjects: sub.plan?.maxProjects ?? null,
              maxDailyRuns: sub.plan?.maxDailyRuns ?? null,
            };
            set({ subscription: sub, planLimits: limits, isLoading: false });
          } catch (e) {
            set({ error: billingError(e), planLimits: null, isLoading: false });
          } finally {
            _subInflight = null;
            _subInflightWsId = null;
          }
        };
        _subInflightWsId = wsId;
        _subInflight = doFetch();
        await _subInflight;
      },

      subscribe: async (wsId, planName, billingMode) => {
        set({ isLoading: true, error: null });
        try {
          const data = await api.post<any>(`/api/workspaces/${wsId}/billing/subscribe`, {
            plan_name: planName,
            billing_mode: billingMode,
          });
          set({ subscription: snakeToCamel(data) as Subscription, isLoading: false });
        } catch (e) {
          set({ error: billingError(e), isLoading: false });
        }
      },

      cancelSubscription: async (wsId) => {
        set({ isLoading: true, error: null });
        try {
          await api.post(`/api/workspaces/${wsId}/billing/cancel`);
          // Re-fetch subscription to get updated status
          await get().fetchSubscription(wsId);
        } catch (e) {
          set({ error: billingError(e), isLoading: false });
        }
      },

      fetchCredits: async (wsId) => {
        if (_creditsInflight && _creditsInflightWsId === wsId) {
          await _creditsInflight;
          return;
        }
        const doFetch = async () => {
          try {
            const data = await api.get<any>(`/api/workspaces/${wsId}/billing/credits`);
            set({ credits: snakeToCamel(data) as CreditBalance });
          } catch (e) {
            console.error("[billingStore] fetchCredits failed:", e);
          } finally {
            _creditsInflight = null;
            _creditsInflightWsId = null;
          }
        };
        _creditsInflightWsId = wsId;
        _creditsInflight = doFetch();
        await _creditsInflight;
      },

      purchaseCredits: async (wsId, amountCents) => {
        set({ error: null });
        try {
          const idempotencyKey = `${wsId}-${amountCents}-${crypto.randomUUID()}`;
          const data = await api.post<any>(`/api/workspaces/${wsId}/billing/credits/purchase`, {
            amount_cents: amountCents,
            idempotency_key: idempotencyKey,
          });
          return snakeToCamel(data) as { clientSecret: string };
        } catch (e) {
          set({ error: billingError(e) });
          return null;
        }
      },

      fetchTransactions: async (wsId, limit = 20, offset = 0) => {
        try {
          const data = await api.get<any[]>(
            `/api/workspaces/${wsId}/billing/credits/transactions?limit=${limit}&offset=${offset}`,
          );
          const txns = (data || []).map((t) => snakeToCamel(t) as CreditTransaction);
          if (offset === 0) {
            set({ transactions: txns });
          } else {
            set((s) => ({ transactions: [...s.transactions, ...txns] }));
          }
          return txns;
        } catch (e) {
          console.error("[billingStore] fetchTransactions failed:", e);
          return [];
        }
      },

      fetchApiKeys: async (wsId) => {
        set({ error: null });
        try {
          const data = await api.get<any[]>(`/api/workspaces/${wsId}/billing/api-keys`);
          set({ apiKeys: (data || []).map((k) => snakeToCamel(k) as ApiKeyInfo) });
        } catch (e) {
          const err = billingError(e);
          if (err === "admin_required") set({ error: err });
        }
      },

      addApiKey: async (wsId, provider, apiKey) => {
        set({ error: null });
        try {
          await api.post(`/api/workspaces/${wsId}/billing/api-keys`, {
            provider,
            api_key: apiKey,
          });
          await get().fetchApiKeys(wsId);
          return true;
        } catch (e) {
          set({ error: billingError(e) });
          return false;
        }
      },

      removeApiKey: async (wsId, provider) => {
        set({ error: null });
        try {
          await api.delete(`/api/workspaces/${wsId}/billing/api-keys/${provider}`);
          set((s) => ({ apiKeys: s.apiKeys.filter((k) => k.provider !== provider) }));
          return true;
        } catch (e) {
          set({ error: billingError(e) });
          return false;
        }
      },

      fetchBillingDetails: async (wsId) => {
        set({ error: null });
        try {
          const data = await api.get<any>(`/api/workspaces/${wsId}/billing/details`);
          set({ billingDetails: snakeToCamel(data) as BillingDetails });
        } catch (e) {
          const err = billingError(e);
          if (err === "admin_required") set({ error: err });
        }
      },

      updateBillingDetails: async (wsId, details) => {
        set({ error: null });
        try {
          const snakeDetails = camelToSnake(details);
          const data = await api.patch<any>(`/api/workspaces/${wsId}/billing/details`, snakeDetails);
          set({ billingDetails: snakeToCamel(data) as BillingDetails });
          return true;
        } catch (e) {
          set({ error: billingError(e) });
          return false;
        }
      },

      fetchUsage: async (wsId, start?, end?) => {
        try {
          const params = new URLSearchParams();
          if (start) params.set("start_date", start);
          if (end) params.set("end_date", end);
          const qs = params.toString();
          const data = await api.get<any>(
            `/api/workspaces/${wsId}/billing/usage${qs ? `?${qs}` : ""}`,
          );
          set({ usage: snakeToCamel(data) as UsageSummary });
        } catch (e) {
          console.error("[billingStore] fetchUsage failed:", e);
        }
      },

      fetchInvoices: async (wsId, limit = 20, offset = 0) => {
        try {
          const data = await api.get<any[]>(
            `/api/workspaces/${wsId}/billing/invoices?limit=${limit}&offset=${offset}`,
          );
          const invs = (data || []).map((i) => snakeToCamel(i) as Invoice);
          if (offset === 0) {
            set({ invoices: invs });
          } else {
            set((s) => ({ invoices: [...s.invoices, ...invs] }));
          }
          return invs;
        } catch (e) {
          console.error("[billingStore] fetchInvoices failed:", e);
          return [];
        }
      },

      updateSeats: async (wsId, seatCount) => {
        set({ isLoading: true, error: null });
        try {
          const data = await api.post<any>(`/api/workspaces/${wsId}/billing/seats`, {
            seat_count: seatCount,
          });
          set({ subscription: snakeToCamel(data) as Subscription, isLoading: false });
        } catch (e) {
          set({ error: billingError(e), isLoading: false });
        }
      },

      fetchMemberUsage: async (wsId, start?, end?) => {
        try {
          const params = new URLSearchParams();
          if (start) params.set("start_date", start);
          if (end) params.set("end_date", end);
          const qs = params.toString();
          const data = await api.get<any[]>(
            `/api/workspaces/${wsId}/billing/usage/members${qs ? `?${qs}` : ""}`,
          );
          set({ memberUsage: (data || []).map((d) => snakeToCamel(d) as MemberUsage) });
        } catch (e) {
          console.error("[billingStore] fetchMemberUsage failed:", e);
        }
      },

      reset: () => set(initialState),
    }),
    {
      name: "colony-billing",
      version: 3,
      partialize: (state) => ({
        subscription: state.subscription,
        credits: state.credits,
        planLimits: state.planLimits,
      }),
      migrate: (persisted: any, version: number) => {
        if (version < 3) {
          // Clear stale planLimits that may have cached max_projects: 3
          return { ...persisted, planLimits: null };
        }
        return persisted;
      },
    },
  ),
);
